import { CommonModule, NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  TemplateRef,
  computed,
  contentChild,
  effect,
  input,
  output,
  signal,
  viewChild,
  OnInit
} from '@angular/core';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideBookOpen,
  lucideHistory,
  lucideImagePlus,
  lucidePen,
  lucidePencil,
  lucidePencilLine,
  lucideSquarePen,
  lucideTrash2,
  lucideUserRoundPen
} from '@ng-icons/lucide';
import { phosphorDotsThreeVertical } from '@ng-icons/phosphor-icons/regular';

import { Tracking } from '@app/shared/types/tracking';

import { OverlayDirective } from '../../directives/overlay/overlay.directive';

const SUPPORTED_ICONS = {
  lucidePen,
  lucideSquarePen,
  lucidePencil,
  lucidePencilLine,
  lucideTrash2,
  lucideBookOpen,
  lucideUserRoundPen,
  lucideImagePlus,
  lucideHistory
} as const;

export interface DropdownItem {
  action: () => Promise<void> | void;
  name: string;
  disabled?: boolean;
  hidden?: boolean;
  tracking?: Tracking;
  icon?: keyof typeof SUPPORTED_ICONS;
}

interface DropdownContext {
  toggleExpanded: () => void;
  expanded: boolean;
}

type DropdownPlacement = 'top' | 'bottom';
type DropdownDirection = 'left' | 'right';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'ui-dropdown',
  standalone: true,
  imports: [NgClass, CommonModule, NgIconComponent, OverlayDirective],
  providers: [
    provideIcons({
      phosphorDotsThreeVertical,
      ...SUPPORTED_ICONS
    })
  ],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent implements OnInit {
  readonly menuElementRef =
    viewChild<ElementRef<HTMLDivElement>>('menuElement');
  readonly containerRef = viewChild<ElementRef<HTMLDivElement>>('container');
  readonly dropdownTrigger =
    contentChild<TemplateRef<unknown>>('dropdownTrigger');
  readonly dropdownContent =
    contentChild<TemplateRef<unknown>>('dropdownContent');

  protected readonly expanded = signal(null);
  protected readonly uniqueId = signal(
    Math.random().toString(36).substring(2, 9)
  );

  readonly placement = signal<DropdownPlacement>('bottom');
  readonly direction = signal<DropdownDirection>('right');

  readonly items = input<DropdownItem[]>([]);
  readonly variant = input<'icon' | 'text' | 'both'>('text');
  readonly name = input<string>('Ações');
  readonly icon = input<string>('phosphorDotsThreeVertical');
  readonly size = input<'base' | 'lg'>('base');
  readonly preferenceDirection = input<DropdownDirection>('right');

  readonly toggle = output<{ expanded: boolean }>();

  readonly isExpanded = computed(() => !!this.expanded());
  readonly showMenuItems = computed(() => !!this.items()?.length);

  readonly hasCustomContent = computed(() => !!this.dropdownContent());

  readonly context = computed<DropdownContext>(() => ({
    toggleExpanded: this.toggleExpanded.bind(this),
    expanded: this.expanded()
  }));

  constructor() {
    effect(() => {
      if (this.expanded() !== null) {
        requestAnimationFrame(() => {
          this.#checkDropdownPosition();
        });
      }
    });
  }

  ngOnInit(): void {
    if (this.expanded() === null) {
      this.expanded.set(false);
    }
  }

  toggleExpanded(): void {
    this.expanded.update((prev) => !prev);

    this.toggle.emit({ expanded: this.expanded() });
  }

  onActionDispatch(action: () => void): void {
    action();
    this.expanded.update((prev) => !prev);
  }

  #checkDropdownPosition(): void {
    const container = this.containerRef()?.nativeElement;
    const menu = this.menuElementRef()?.nativeElement;

    if (!container || !menu) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const menuWidth = menuRect.width;
    const menuHeight = menuRect.height;

    const SAFETY_MARGIN = 16;

    const spaceBelow =
      window.innerHeight - containerRect.bottom - SAFETY_MARGIN;
    const spaceAbove = containerRect.top - SAFETY_MARGIN;

    const spaceRight = window.innerWidth - containerRect.right - SAFETY_MARGIN;
    const spaceLeft = containerRect.left - SAFETY_MARGIN;

    // Lógica de posicionamento vertical
    if (spaceBelow < menuHeight && spaceAbove >= menuHeight) {
      this.placement.set('top');
    } else {
      this.placement.set('bottom');
    }

    // Lógica de posicionamento horizontal
    if (spaceRight >= menuWidth && spaceLeft >= menuWidth) {
      this.direction.set(this.preferenceDirection());
    } else if (spaceRight >= menuWidth) {
      // Prioriza direita se couber
      this.direction.set('right');
    } else if (spaceLeft >= menuWidth) {
      // Se não couber na direita mas couber na esquerda
      this.direction.set('left');
    } else {
      // Se não couber em nenhum lado, ajusta para o lado com mais espaço
      this.direction.set(spaceRight > spaceLeft ? 'right' : 'left');

      // Redimensiona o menu se necessário
      if (spaceRight > spaceLeft) {
        menu.style.maxWidth = `${spaceRight + SAFETY_MARGIN}px`;
      } else {
        menu.style.maxWidth = `${spaceLeft + SAFETY_MARGIN}px`;
      }
    }
  }
}
